import { useState } from "react";
import { Target, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Brain, CircleNotch } from "@phosphor-icons/react";
import { AnalyzeResumeResult, MetricKeyEnum } from "@reactive-resume/schema";
import ReactMarkdown from "react-markdown";
import { t } from "@lingui/macro";
import { useTheme } from "@reactive-resume/hooks";
import { useAnalysisContext } from "@/client/contexts/analysis-context";
import { cn } from "@reactive-resume/utils";

// Reusable component to display metric score and reasoning
export const MetricScoreCard = ({
  metricId,
  analysesData,
}: {
  metricId: MetricKeyEnum;
  analysesData: AnalyzeResumeResult;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDarkMode } = useTheme();
  const { handleFocusedMetricOptimize, isOptimizing } = useAnalysisContext();
  const metricData = analysesData.metrics[metricId];

  if (!metricData) return null;

  // Calculate score from weightedScore and weight
  const score = metricData.weight > 0 ? metricData.weightedScore / metricData.weight : 0;
  const scorePercentage = Math.round(score * 100);

  // Get reasoning from the metric object
  const reasoning =
    (metricData.object as any)?.reasoning || t`No detailed analysis available for this metric.`;

  // Get description from the backend response
  const description =
    metricData.description || t`Analysis of how your resume matches the job requirements`;

  // Define colors
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    red: isDarkMode ? "#ef5350" : "#e57373",
    amber: "#ffb74d",
    blue: "#4dabf5",
    greenBg: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
    redBg: isDarkMode ? "rgba(239, 83, 80, 0.15)" : "rgba(229, 115, 115, 0.1)",
    amberBg: isDarkMode ? "rgba(255, 183, 77, 0.15)" : "rgba(255, 183, 77, 0.1)",
    blueBg: isDarkMode ? "rgba(77, 171, 245, 0.15)" : "rgba(77, 171, 245, 0.1)",
  };

  // Determine status based on score
  let status: "excellent" | "good" | "needs_work" = "needs_work";
  if (scorePercentage >= 80) status = "excellent";
  else if (scorePercentage >= 60) status = "good";

  // Get dynamic button text based on score
  const getButtonText = () => {
    if (scorePercentage >= 80) return t`Perfect This`;
    if (scorePercentage >= 60) return t`Boost This`;
    return t`Address This Issue`;
  };

  // Get status info
  const getStatusInfo = () => {
    switch (status) {
      case "excellent":
        return {
          text: t`Excellent`,
          description: t`This aspect of your resume is working very well for this job.`,
          action: t`Keep doing what you're doing - this is a strength!`,
          bgColor: colors.greenBg,
          textColor: colors.green,
          icon: CheckCircle2,
        };
      case "good":
        return {
          text: t`Good`,
          description: t`This area is decent but could be improved for better results.`,
          action: t`Review the suggestions below to make this even stronger.`,
          bgColor: colors.blueBg,
          textColor: colors.blue,
          icon: Target,
        };
      default:
        return {
          text: t`Needs Work`,
          description: t`This area needs significant improvement to match the job requirements.`,
          action: t`Focus on the recommendations below to improve your chances.`,
          bgColor: colors.redBg,
          textColor: colors.red,
          icon: AlertTriangle,
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  // Get metric name for display

  // Truncate reasoning for initial display
  const TRUNCATE_LENGTH = 200;
  const truncatedReasoning =
    reasoning.length > TRUNCATE_LENGTH
      ? reasoning.substring(0, TRUNCATE_LENGTH) + "..."
      : reasoning;
  const showSeeMore = reasoning.length > TRUNCATE_LENGTH;

  return (
    <div className="rounded-lg border border-slate-200 p-3 shadow-sm dark:border-slate-700">
      {/* Header with metric name and score */}
      <div className="mb-2">
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="inline-flex items-center justify-center rounded-md px-3 py-2 text-xl font-bold"
              style={{
                backgroundColor: statusInfo.bgColor,
                color: statusInfo.textColor,
                border: `1px solid ${statusInfo.textColor}20`,
              }}
            >
              {scorePercentage}%
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {metricData.metric}
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400">{description}</p>
            </div>
          </div>
          <div className="ml-4 flex items-center">
            {/* Optimize this metric button */}
            <button
              className={cn(
                "group relative w-full max-w-80 transform cursor-pointer overflow-hidden rounded-xl border px-6 py-2 font-semibold shadow-xl transition-transform duration-300 hover:scale-[1.04] focus:ring-4 focus:outline-none dark:text-white",
                "border-gray-300 bg-gradient-to-r from-slate-200 via-slate-200 to-slate-200 text-slate-700 focus:ring-blue-200",
                "dark:border-slate-600! dark:bg-gradient-to-r dark:from-slate-800 dark:via-slate-600 dark:to-slate-700 dark:hover:from-slate-700 dark:hover:to-slate-600 dark:focus:ring-slate-400",
              )}
              onClick={() => handleFocusedMetricOptimize(metricId)}
              disabled={isOptimizing}
              aria-label={
                isOptimizing
                  ? t`Currently optimizing this metric with AI`
                  : t`Optimize this metric with AI`
              }
            >
              <div className="animate-shimmer absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent dark:via-white/10"></div>
              <span className="relative z-10 flex items-center justify-center gap-2">
                {isOptimizing ? (
                  <>
                    <CircleNotch className="size-5 animate-spin" />
                    <span>{t`Optimizing...`}</span>
                  </>
                ) : (
                  <>
                    <Brain
                      className="size-5 animate-pulse transition-all duration-300"
                      weight="duotone"
                      aria-hidden="true"
                    />
                    <span className="relative z-10">{getButtonText()}</span>
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Analysis content with truncation */}
      <div className="mt-3 rounded-md bg-slate-50 p-3 dark:bg-slate-800/50">
        <div className="prose prose-sm prose-slate dark:prose-invert max-w-none">
          <ReactMarkdown
            components={{
              p: ({ children }) => (
                <p className="mb-2 text-sm leading-relaxed text-slate-700 last:mb-0 dark:text-slate-300">
                  {children}
                </p>
              ),
              ul: ({ children }) => (
                <ul className="mb-2 space-y-0.5 pl-3 text-sm text-slate-700 last:mb-0 dark:text-slate-300">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="mb-2 space-y-0.5 pl-3 text-sm text-slate-700 last:mb-0 dark:text-slate-300">
                  {children}
                </ol>
              ),
              li: ({ children }) => <li className="leading-relaxed">{children}</li>,
              h1: ({ children }) => (
                <h1 className="mb-2 text-base font-semibold text-slate-900 dark:text-slate-100">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="mb-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {children}
                </h3>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-slate-900 dark:text-slate-100">
                  {children}
                </strong>
              ),
            }}
          >
            {isExpanded ? reasoning : truncatedReasoning}
          </ReactMarkdown>

          {showSeeMore && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800 focus:underline focus:outline-none dark:text-blue-400 dark:hover:text-blue-200"
            >
              {isExpanded ? t`See less` : t`See more`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
